import { Fragment, FunctionalComponent, h } from "preact";
import { lazy, Suspense } from "preact/compat";
import Router, { route, Route, RouterOnChangeArgs } from "preact-router";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setAliasMode,
  setPortalAlias,
  setPortalId,
  setWorkspaceAlias,
  setWorkspaceId,
} from "../../redux/slices/aliasSlice";
import {
  setMode,
  setPreview,
  setNarrowFactor,
  setWsd,
} from "../../redux/slices/appearanceSlice";

import { setAzureSasToken } from "../../redux/slices/authSlice";

import { get_DASHBOARD_URI_BASE, getIsEdge } from "../../configs/env";
import {
  Utils,
  checkPublicUserId,
  getEdgeUserLoginSession,
  isEdgeUserSessionExpired,
  saveEdgeUserLoginSession,
  setTheme,
} from "../../utils";

import axios from "axios";
import logger from "../../logger";

import { getIsPreview, getEffectiveMode } from "./previewMode";

import {
  setClientIp,
  setUserId,
  setUserIdRan,
  setCustomUserId,
} from "../../redux/slices/globalsSlice";
import { useState, useEffect } from "preact/hooks";

// Lazy load all route components for code splitting
const Home = lazy(() => import("../../routes/home"));
const Search = lazy(() => import("../../routes/search"));
const Pdf = lazy(() => import("../../routes/pdf"));
const Video = lazy(() => import("../../routes/video"));
const Sheet = lazy(() => import("../../routes/sheet"));
const Notfound = lazy(() => import("../../routes/notfound"));
const CustomError = lazy(() => import("../../routes/customError"));
const Login = lazy(() => import("../../routes/login"));
const Chatbot = lazy(() => import("../../routes/chatbot"));
const KorraCopilot = lazy(() => import("../../routes/korra-copilot"));
const LoginPage = lazy(() => import("../../routes/edgeLoginPage"));
const Portal = lazy(() => import("../../routes/analytics_app"));
const KeywordSearch = lazy(
  () => import("../../routes/keywordSearch/keywordSearch"),
);
const KeywordLayout = lazy(() => import("../../routes/home/keywordLayout"));

// Simple loading spinner
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <div>Loading...</div>
  </div>
);

// Suspense fallback that renders nothing until `delay` ms have passed, so fast
// chunk loads (cached / already-preloaded) never flash the spinner. Only loads
// slower than the threshold ever show it.
const DelayedFallback = ({ delay = 200 }: { delay?: number }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return show ? <LoadingSpinner /> : null;
};

// Route chunk loaders. Exposed so nav elements can preload on hover/focus.
export const routePreloaders: Record<string, () => Promise<unknown>> = {
  home: () => import("../../routes/home"),
  search: () => import("../../routes/search"),
  keywordSearch: () => import("../../routes/keywordSearch/keywordSearch"),
  keywordLayout: () => import("../../routes/home/keywordLayout"),
  pdf: () => import("../../routes/pdf"),
  video: () => import("../../routes/video"),
  sheet: () => import("../../routes/sheet"),
  chatbot: () => import("../../routes/chatbot"),
  korraCopilot: () => import("../../routes/korra-copilot"),
  analyticsApp: () => import("../../routes/analytics_app"),
};

// Preload route chunks only once the current page has fully loaded and the
// browser is idle, so preloading never competes with the current page's load.
const preloadRoutes = () => {
  const runWhenIdle = () => {
    const schedule =
      window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    // One chunk per idle slice so the queue can't monopolize the main thread.
    Object.values(routePreloaders).forEach((load) =>
      schedule(() => {
        load().catch(() => {}); // ignore failed preloads; real nav will surface the error
      }),
    );
  };

  if (document.readyState === "complete") {
    runWhenIdle();
  } else {
    window.addEventListener("load", runWhenIdle, { once: true });
  }
};

export interface RouterProps {
  workspaceId: string;
  kappId: string;
  portalId: string;
  workspaceAlias: string;
  portalAlias: string;

  // editor
  preview?: string;

  // search
  q: string;
  back: boolean;
  // pdf / video
  guid: string;

  // pdf
  docId: string; // docGuid
  page: string;

  // video
  docGuid: string;

  // widget
  mode: "widget";

  // narrowfactor
  narrowfactor: string;

  sasTokenPreview?: string;
  chatbotFlag?: boolean;
  userID?: string;

  // korra-copilot
  model?: string;
  problem?: string;
}
const AppRouter: FunctionalComponent = () => {
  const appearance = useAppSelector((state) => state.appearance);
  const globals = useAppSelector((state) => state.globals);
  const alias = useAppSelector((state) => state.alias);
  const auth = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [isEdge, setIsEdge] = useState<boolean | undefined>(getIsEdge());

  // Preload other route chunks after the current page has loaded (idle-time),
  // so first navigation doesn't flash the Suspense fallback.
  useEffect(() => {
    preloadRoutes();
  }, []);

  /**
   * error 400
   * @param routerProps
   */
  const route400 = (routerProps: RouterProps) => {
    logger.warn({
      message: "Something wrong with routerProps",
      router_props: routerProps,
      portal_alias: alias.portalAlias,
      workspace_id: alias.workspaceId,
    });
    route(`/error/${400}/errorMessage/${"Bad URL."}`);
  };

  const route422 = (errorMessage: string) => {
    route(`/error/${422}/errorMessage/${errorMessage}`);
  };

  const isSasTokenExpired = (timestamp: number) => {
    const now = new Date().getTime();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 1000 / 60);
    return minutes > 30;
  };

  /**
   * on route change
   * @param args
   * @returns
   */
  const onChange = async (args: RouterOnChangeArgs) => {
    try {
      const matches = (args.current.props as any)?.matches;
      if (isEdge) {
        // Mosaic-to-Edge login: the auth service redirects here with a one-time
        // token in the URL fragment (#token=<jwt>&workspaceId=...). The fragment
        // is never sent to the server, so the JWT can't leak via proxy logs or
        // the Referer header. Consume it into an edge session so the user lands
        // logged in, then strip the fragment from the URL.
        const rawHash = window.location.hash.startsWith("#")
          ? window.location.hash.slice(1)
          : window.location.hash;
        const hashAuthParams = new URLSearchParams(rawHash);
        const tokenFromUrl = hashAuthParams.get("token");
        if (tokenFromUrl) {
          const wsId = hashAuthParams.get("workspaceId") ?? "";
          const mosaicUser = {
            workspaceId: wsId,
            // Identity comes from the fragment too; edge code paths key off
            // user.email (e.g. CDN SAS-token requests), so it must be set.
            email: hashAuthParams.get("email") ?? "",
            password: "",
            id: "",
            firstName: hashAuthParams.get("firstName") ?? "",
            lastName: hashAuthParams.get("lastName") ?? "",
            name: hashAuthParams.get("name") ?? "",
            isReseted: false,
            userIdToken: tokenFromUrl,
          };
          // The auth service redirects here (to "/") carrying only a workspaceId,
          // but every web-app route is /:workspaceAlias/:portalAlias. Resolve the
          // workspace's portal so we can (a) key the session by its portalAlias so
          // the guard below passes and (b) land on the real portal route instead
          // of bouncing to /edge/login.
          let landingPortalAlias = matches?.portalAlias ?? "";
          let landingWorkspaceAlias = "";
          try {
            const aliasRes = await axios.get(
              `${get_DASHBOARD_URI_BASE()}/alias-list-by-name/_`,
            );
            // The endpoint returns one entry per portal, so a workspace with
            // multiple portals yields multiple matches, and the Mosaic token
            // doesn't name a portal. Pick deterministically (cursor order isn't
            // stable):
            //   1. a portal whose alias matches the workspace alias, else
            //   2. prefer mode === "portal", then the lowest portalAlias
            // so the same user always lands in the same portal.
            const candidates = (aliasRes?.data?.data ?? []).filter(
              (p: any) =>
                p?.workspaceId === wsId && p?.portalAlias && p?.workspaceAlias,
            );
            candidates.sort((a: any, b: any) => {
              const aMatchesWs = a.portalAlias === a.workspaceAlias ? 0 : 1;
              const bMatchesWs = b.portalAlias === b.workspaceAlias ? 0 : 1;
              return (
                aMatchesWs - bMatchesWs ||
                (a.mode === "portal" ? 0 : 1) - (b.mode === "portal" ? 0 : 1) ||
                String(a.portalAlias).localeCompare(String(b.portalAlias))
              );
            });
            const pair = candidates[0];
            if (pair) {
              landingPortalAlias = pair.portalAlias;
              landingWorkspaceAlias = pair.workspaceAlias;
            }
          } catch (err) {
            logger.error({
              message: "Mosaic login: failed to resolve workspace portal",
              error: (err as any)?.message,
              stacktrace: (err as any)?.stack,
              workspace_id: wsId,
            });
          }
          // Key the session by the portal alias we're about to land on so the
          // workspaceId guard below passes.
          saveEdgeUserLoginSession(mosaicUser, landingPortalAlias);
          // Drop the fragment so the token isn't left in the address bar/history.
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, "", cleanUrl);
          // Land the user straight in their portal (logged in). Without this the
          // bare "/" landing matches no route and would bounce to /edge/login.
          if (landingWorkspaceAlias && landingPortalAlias) {
            route(`/${landingWorkspaceAlias}/${landingPortalAlias}`, true);
            return;
          }
        }
        const user = await getEdgeUserLoginSession();
        const onLoginPage = args.path?.includes("/edge/login");
        if (user && !isEdgeUserSessionExpired(user?.timestamp)) {
          if (user.workspaceId !== matches?.portalAlias) {
            route("/edge/login", true);
          }
          logger.log({
            message: "App router changed",
            user: user,
            portal_alias: alias.portalAlias,
            workspace_id: user?.workspaceId,
          });
        } else if (!onLoginPage) {
          // Not signed in: send to login, preserving the target URL so we can
          // return the user there after a successful login.
          const target =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          const loginUrl =
            target && target !== "/"
              ? `/edge/login?redirect=${encodeURIComponent(target)}`
              : "/edge/login";
          route(loginUrl, true);
        }
      }
      if (args.path?.includes("/login")) return;
      if (matches) {
        const routerProps = {
          workspaceId: matches?.workspaceId,
          kappId: matches?.kappId,
          portalId: matches?.portalId,
          workspaceAlias: (args.current.props as any)?.matches?.workspaceAlias,
          portalAlias: matches?.portalAlias,
          preview: matches?.preview,
          q: matches?.q,
          back: matches?.back,
          guid: matches?.guid,
          docId: matches?.docId,
          page: matches?.page,
          docGuid: matches?.docGuid,
          mode: matches?.mode,
          narrowfactor: matches?.narrowfactor,
          sasTokenPreview: matches?.sasTokenPreview,
          chatbotFlag: args.path?.includes("/chatbot"),
          userID: matches?.userID,
          model: matches?.model,
          problem: matches?.problem,
        } as RouterProps;
        const isPreview = getIsPreview(
          routerProps.preview,
          window.location.search,
        );
        if (isPreview) {
          dispatch(setPreview(true));
          if (routerProps.sasTokenPreview) {
            dispatch(
              setAzureSasToken(decodeURIComponent(routerProps.sasTokenPreview)),
            );
          }
        }

        if (appearance.loaded && !appearance.isPrivate && !globals.userIdRan) {
          const userID = checkPublicUserId();
          dispatch(setUserId(userID));

          //to make user we dont run the useEffect more than once just when appearance.loaded === true
          dispatch(setUserIdRan(true));
        }

        if (!appearance.narrowfactor && routerProps.narrowfactor == "1") {
          dispatch(setNarrowFactor(1));
        }

        if (routerProps.mode && routerProps.mode !== appearance.mode)
          dispatch(setMode(routerProps.mode));

        const urlParams = new URLSearchParams(window.location.search);
        const wsdFromUrl = urlParams.get("wsd") === "true";
        if (wsdFromUrl !== appearance.wsd) {
          dispatch(setWsd(wsdFromUrl));
        }

        if (routerProps.userID) {
          localStorage.setItem("korraCustomUserId", routerProps.userID);
          dispatch(setCustomUserId(routerProps.userID));
        }

        if (!globals.clientIp && !isEdge) {
          try {
            const _clientIp = await axios.get(
              "https://api.ipify.org?format=json",
            );
            if (_clientIp?.data?.ip) {
              dispatch(setClientIp(_clientIp.data.ip));
            }
          } catch (error) {
            logger.error({
              message: "Failed to get client IP",
              error: error,
              stacktrace: (error as any)?.stack,
              portal_alias: alias.portalAlias,
              workspace_id: alias.workspaceId,
            });
          }
        }

        if (
          routerProps.workspaceAlias &&
          routerProps.portalAlias &&
          !alias.workspaceId &&
          !alias.portalId
        ) {
          const debugg = get_DASHBOARD_URI_BASE();
          const url = `${get_DASHBOARD_URI_BASE()}/alias/${
            routerProps.workspaceAlias
          }/${routerProps.portalAlias}`;
          logger.log({
            message: "Alias lookup URLs",
            alias_url: url,
            workspace_alias: routerProps.workspaceAlias,
            portal_alias: routerProps.portalAlias,
          });
          const _aliasResponce = await axios(
            `${get_DASHBOARD_URI_BASE()}/alias/${routerProps.workspaceAlias}/${
              routerProps.portalAlias
            }`,
          );
          const aliasResponse = _aliasResponce?.data?.data;

          if (aliasResponse?.workspaceId && aliasResponse?.portalId) {
            // Get the portal details with themes
            const portalResponse = await axios(
              `${get_DASHBOARD_URI_BASE()}/portals/${
                aliasResponse.portalId
              }/workspaces/${aliasResponse.workspaceId}`,
            );
            const portalData = portalResponse?.data?.data;

            dispatch(setWorkspaceId(aliasResponse.workspaceId));
            dispatch(setPortalId(aliasResponse.portalId));
            dispatch(setWorkspaceAlias(routerProps.workspaceAlias));
            dispatch(setPortalAlias(routerProps.portalAlias));
            const effectiveMode =
              getEffectiveMode({
                isPreview,
                mode: portalData.mode,
                previewMode: portalData.previewMode,
              }) ?? "";
            dispatch(setAliasMode(effectiveMode));

            // Get the correct theme based on mode
            let selectedTheme;
            if (effectiveMode === "chatbot") {
              selectedTheme = portalData.chatThemes.find(
                (t: any) => t?.name === "publish",
              );
            } else {
              selectedTheme = portalData.themes.find(
                (t: any) => t?.name === "publish",
              );
            }

            if (selectedTheme) {
              setTheme(selectedTheme, dispatch, aliasResponse.workspaceId);
            }

            const isPrivate = selectedTheme?.isPrivate;

            if (isPrivate) {
              if (isPreview) {
                dispatch(setPreview(true));
                return;
              }

              if (!auth.korraUser && !auth.auth0User && !isEdge) {
                Utils.routeToLogin({
                  workspaceAlias: routerProps.workspaceAlias,
                  portalAlias: routerProps.portalAlias,
                });
              }
            }
            if (!isPrivate && !globals.userIdRan) {
              const userID = checkPublicUserId();
              dispatch(setUserId(userID));

              dispatch(setUserIdRan(true));
            }
          } else {
            route422("It is alias but couldn't get alias response");
          }
        } else if (
          routerProps.workspaceId &&
          (routerProps.portalId || routerProps.kappId)
        ) {
          dispatch(setWorkspaceId(routerProps.workspaceId));
          if (routerProps.portalId) {
            dispatch(setPortalId(routerProps.portalId));

            const theme = await Utils.getTheme(
              routerProps.workspaceId,
              routerProps.portalId,
              !routerProps.mode,
            );

            if (theme) {
              setTheme(theme, dispatch, routerProps.workspaceId);
            }
            if (theme?.isPrivate) {
              if (isPreview) {
                dispatch(setPreview(true));
                return;
              }
              if (!auth.korraUser && !auth.auth0User && !isEdge) {
                Utils.routeToLogin(null);
              }
            }
            if (!theme?.isPrivate && !globals.userIdRan) {
              const userID = checkPublicUserId();
              dispatch(setUserId(userID));

              //to make user we dont run the useEffect more than once just when appearance.loaded === true
              dispatch(setUserIdRan(true));
            }
          } else if (routerProps.kappId) {
            // dispatch(setKappId(routerProps.kappId));
            // no need theme, no need alias, all is ok, it is kapp
          } else {
            route400(routerProps);
          }
        } else {
          if (
            appearance.isPrivate &&
            (!alias.workspaceAlias || !alias.workspaceId)
          ) {
            if (isPreview) {
              dispatch(setPreview(true));
              return;
            }
            route400(routerProps);
          }
        }
      } else {
        route422("No matches at URL params");
      }
    } catch (error: any) {
      logger.error({
        message: "Error in router onChange handler",
        error: error,
        stacktrace: error?.stack,
        portal_alias: alias.portalAlias,
        workspace_id: alias.workspaceId,
        path: args.path,
        error_detail: error?.message || "",
      });
      route422("Something wrong: " + error?.message || "");
    }
  };

  return (
    <Suspense fallback={<DelayedFallback />}>
      <Router onChange={onChange}>
        <Route path="/workspace/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/chatbot"
          component={Chatbot}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/analytics_app"
          component={Portal}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/generalAIHome"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/generalAISearch"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />

        <Route
          path="/workspace/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        <Route path="/company/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/company/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route path="/company/:workspaceId/portal/:portalId" component={Home} />
        <Route
          path="/company/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route path="/analytics_app" component={Portal} />
        <Route
          path="/:workspaceAlias/:portalAlias/analytics_app"
          component={Portal}
        />

        {/*  */}

        <Route path="/:workspaceAlias/:portalAlias" component={Home} />
        <Route path="/:workspaceAlias/:portalAlias/home" component={Home} />
        <Route
          path="/:workspaceAlias/:portalAlias/generalAIHome"
          component={Home}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/keywordHome"
          component={KeywordLayout}
        />
        <Route path="/:workspaceAlias/:portalAlias/search" component={Search} />
        <Route
          path="/:workspaceAlias/:portalAlias/generalAISearch"
          component={Search}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/keywordSearch"
          component={KeywordSearch}
        />
        <Route path="/:workspaceAlias/:portalAlias/login" component={Login} />
        <Route
          path="/:workspaceAlias/:portalAlias/chatbot"
          component={Chatbot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/analytics_app"
          component={Portal}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/korra-copilot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/analytics_app?mode=widget"
          component={Portal}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/chatbot?mode=widget"
          component={Chatbot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Video}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Pdf}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Sheet}
        />

        {/*  */}

        <Route
          path="/error/:statusCode/errorMessage/:errorMessage"
          component={CustomError}
        />

        {isEdge ? <LoginPage default /> : <Notfound default />}

        {/*  */}
        <Route
          path="/:workspaceAlias/:portalAlias/login/redirect/:redirect"
          component={Login}
        />
        <Route path="/login/redirect/:redirect" component={Login} />
        <Route path="/login" component={Login} />

        {/* edge routes */}
        <Route path="/edge/login" component={LoginPage} />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/chatbot"
          component={Chatbot}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />

        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        <Route path="/app/company/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        {/*  */}

        <Route path="/app/:workspaceAlias/:portalAlias" component={Home} />
        <Route path="/app/:workspaceAlias/:portalAlias/home" component={Home} />
        <Route
          path="/app/:workspaceAlias/:portalAlias/search"
          component={Search}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/keywordSearch"
          component={KeywordSearch}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/login"
          component={Login}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/chatbot"
          component={Chatbot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/korra-copilot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/chatbot?mode=widget"
          component={Chatbot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Video}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Pdf}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Sheet}
        />

        {/*  */}

        <Route
          path="/app/error/:statusCode/errorMessage/:errorMessage"
          component={CustomError}
        />

        {/*  */}
        <Route
          path="/app/:workspaceAlias/:portalAlias/login/redirect/:redirect"
          component={Login}
        />
        <Route path="/app/login/redirect/:redirect" component={Login} />
        <Route path="/app/login" component={Login} />

        {/* private */}
      </Router>
    </Suspense>
  );
};

export default AppRouter;
